import { useEffect, useMemo } from 'react'
import { IProduct, ProductPromotionType } from '@core/api/Products/types'
import { LYSDivider, LYSTypography } from '@core/components/Primitives'
import { useRelatedProducts } from '@core/components/ProductDetail/useRelatedProducts'
import PromotionContext from '@core/components/shared/PromotionContext'
import { productCollectionViewEvent } from '@core/events/product'
import { useTranslation } from '@core/i18n/i18n'
import { removeEmpty, removeObjectDuplicates } from '@core/utils/arrays'
import { useServices } from '@core/utils/ioc'
import { useProductDetailContext } from '@core/utils/models/product/ProductContext'
import { usePageContext } from '@core/utils/pages/PageContext'
import { RenderProp, Slot, template } from '@core/utils/templates'
import ProductVariationSwitchItem from './ProductVariationSwitchItem'

export interface ProductVariationSwitchSlots {
  headline: RenderProp<void>
  list: RenderProp<void>
}

export interface ProductVariationSwitchComponents {
  ProductVariationSwitchItem: typeof ProductVariationSwitchItem
}

export interface ProductVariationSwitchProps {
  className: string
  withBottomDivider?: boolean
}

const useProductVariations = (product?: IProduct) => {
  const { relatedProducts } = useRelatedProducts(product, 'variations')
  return useMemo(() => {
    // If the only variation is the product itself, then don't display anything
    const onlyVariationIsSelf =
      relatedProducts.length === 1 && relatedProducts[0].id === product?.id

    if (onlyVariationIsSelf) return []

    // Remove empty and duplicate hits, sort by SKU
    const clearedVariations = removeEmpty([...relatedProducts])
    const preSortedVariations = clearedVariations.sort((a, b) =>
      a.sku < b.sku ? -1 : 1
    )
    return removeObjectDuplicates(preSortedVariations, 'id')
  }, [product, relatedProducts])
}

const ProductVariationSwitch = template<
  ProductVariationSwitchProps,
  ProductVariationSwitchSlots,
  ProductVariationSwitchComponents
>(({ className, withBottomDivider, slots, getComponents }) => {
  const { t } = useTranslation()
  const { eventBus } = useServices()
  const { product } = useProductDetailContext() || {}
  const productVariations = useProductVariations(product)
  const pageContext = usePageContext()

  useEffect(() => {
    if (productVariations?.length) {
      eventBus.publish(
        productCollectionViewEvent(
          productVariations,
          {
            isPromotion: true,
            promotionType: ProductPromotionType.Variations,
          },
          pageContext
        )
      )
    }
  }, [eventBus, productVariations, pageContext])

  if (!product || !productVariations || !productVariations.length) {
    return null
  }

  const components = getComponents({
    ProductVariationSwitchItem,
  })

  return (
    <PromotionContext.Provider
      value={{
        isPromotion: true,
        promotionType: ProductPromotionType.Variations,
      }}
    >
      <div className={className}>
        <Slot render={slots?.headline}>
          <LYSTypography.Title level={3} visualAppearance={'h4'}>
            {t('productDetail.productVariations')}
          </LYSTypography.Title>
        </Slot>
        <Slot render={slots?.list}>
          {productVariations.map((variation) => (
            <components.ProductVariationSwitchItem
              product={variation}
              isSelected={variation.id === product.id}
              key={variation.id}
            />
          ))}
        </Slot>
        {withBottomDivider && <LYSDivider />}
      </div>
    </PromotionContext.Provider>
  )
})

export default ProductVariationSwitch
